/* ============================================================
   圖鑑文案 —— 與造型分開的一層
   ============================================================

   為什麼不寫進 `entities/families/*.ts`：那些檔是**機械移植的造型**
   （`tools/port-families.mjs` 產生，不能手改），文案寫進去下一次移植就沒了。
   使用者要改文案時只會開這一個檔。

   三段解鎖（使用者 2026-08-02 指定）：
     沒看過   大問號，什麼都沒有
     看過     黑影 —— **只有家族與遭遇次數**，威脅色也鎖著
     打死過   全開：數值條 + 威脅色通則 + 這裡的一句手寫

   手寫那一句的判準：**它要能改變玩家下一次遇到牠的動作**。
   寫「牠很快」沒有用，寫「預備只有 0.26 秒，看到才閃已經來不及」才有用。 */

/** 四色威脅語彙的通則（規格 §2.3）。每一隻打死後都會套上對應的一條。 */
export var THREAT_RULE = {
  red: { zh: '接觸', rule: '碰到你才有傷害。它沒有遠程手段 —— 距離就是解。' },
  amb: { zh: '直射', rule: '直線彈道，會被地形擋。橫向移動一步就躲得掉，往後退躲不掉。' },
  vio: { zh: '範圍', rule: '地面會先亮出預警圈。圈是給你看的，走出去就好 —— 別站著硬吃。' },
  azu: { zh: '支援', rule: '自己不傷你，但讓別人變難打。場面失控時先解決它，不是先解決最近的。' }
};

/** 八個剪影家族（規格 §2.2）。**看過就解鎖家族**，這是第二段的全部內容 ——
    「我知道它是什麼形狀的東西，但不知道它會做什麼」正是那一段該有的資訊量。 */
export var FAM_INFO = {
  spike: { zh: '錐', sil: '細長前傾＋單一前指尖端', note: '全表唯一前後長於左右的外框，俯視也認得出來。' },
  wedge: { zh: '楔', sil: '低寬貼地＋正面斜面', note: '整體是斜的 —— 傾斜裝甲在 20px 下唯一還讀得到的形式。' },
  wall: { zh: '壁', sil: '直立大平面', note: '唯一的垂直大矩形。它擋的東西比它打的東西重要。' },
  ladle: { zh: '勺', sil: '蹲坐底座＋往斜上長出去的長件', note: '底座不動、長件會抬 —— 抬起來就是它要出手了。' },
  orb: { zh: '球', sil: '圓', note: '沒有正面也沒有背面，繞後對它沒有意義。' },
  bar: { zh: '桿', sil: '水平長桿', note: '寬度就是威脅範圍。它的攻擊幾乎都沿著那條桿發生。' },
  wing: { zh: '翼', sil: '浮空', note: '辨識靠 Y 軸不靠外框。**看影子** —— 影子離身體越遠，它飛得越高。' },
  ring: { zh: '環', sil: '環', note: '環心是它的功能所在：射擊、吸引、孵化都從中間出來。' }
};

/** 深層階層（規格 §2.2b）。**不是第九個家族**，是疊在家族上的第三軸。 */
export var DEEP_RULE = {
  zh: '深層',
  rule: '身上有一個不受光的洞，而且左右不對稱 —— 腿長不同、翼一大一小、突刺往一邊偏。'
    + '這兩件事都不是損傷，是它原本就長這樣。'
};

/** 每一隻的一句應對提示。key = 型別 id。 */
export var CODEX_TIP = {
  /* SPIKE 錐 */
  nadel: '預備只有 0.26 秒 —— 看到才閃已經來不及。它的解法是「不要讓它進到身前」，而不是反應。',
  lanze: '砲口環放大到最亮的那一刻就是它要射了，將近一秒。那一秒足夠你橫向走出彈道，但不夠你轉身跑。',
  geissel: '噴口在噴之前會先張開。扇形是往前的 —— 繞側面比拉開距離便宜得多。',
  fuhler: '它自己一發都不會打你。頂端那顆珠開始脈動代表它正在替別人標定 —— 那一刻的優先度高於任何雜兵。',
  /* WEDGE 楔 */
  keil: '衝刺前整片斜甲會先掀起來。它衝的是一條直線而且不會轉彎，往側面走一步就過去了。',
  pflug: '橫置雙管是**掃過來**的，不是一次打完。往掃的反方向跑會一路吃滿，往它掃過的那一側跑最安全。',
  amboss: '它跳起來的瞬間地上就出現落點圈。圈的位置在牠起跳那一刻就定了 —— 走出去就好，不用預判。',
  /* WALL 壁 */
  ramme: '撞桿從盾後穿出來才是真正的攻擊起手，盾本身不會傷你。它比 KEIL 慢但推得更遠。',
  luke: '盾裂成上下兩半、砲管伸出來的那一刻要離開它的正面。缺口關上時它完全打不到你，那是繞後的窗口。',
  schild: '它一發都不會打你，但它罩住的東西會變得很難打。**先繞到它後面**，不要對著盾多打幾發。',
  /* LADLE 勺 */
  haken: '抓鉤把你往它那邊拖 —— 危險的不是鉤子，是它拖你去的地方。躲到掩體後面就拉不動了。',
  morser: '曲射不看地形，躲柱子沒有用。地上的圈亮起來之後你有將近一秒可以走出去。',
  bake: '天線立起來時它正在替周圍的敵人加成。它離戰線最遠，也最值得專程去拆。',
  /* ORB 球 */
  rolle: '雙箍的自轉速度就是它的速度。滾起來之後不會轉彎，側移一步比往後退有效得多。',
  auge: '瞳孔放大到最大就是要射了。它浮在空中，地面掩體對它無效 —— 要嘛打掉，要嘛離開它的視線。',
  dorn: '赤道環上的引信從 4 Hz 加速到 26 Hz，那是倒數。**打死它一樣會炸** —— 正解是拉開距離，不是補刀。',
  blase: '交叉雙環撐開時它在給周圍的東西上護盾。它自己很脆，先拆它比硬打被護住的東西快。',
  /* BAR 桿 */
  sense: '橫刃轉速拉到十三倍的時候，它的判定會擴到整條桿。那一秒不要待在桿掃得到的圓裡。',
  spule: '三個線圈依序亮起＝倒數三拍。它是全表射程最遠的雜兵，而且傷害最高 —— 但它也最脆。',
  harfe: '三管齊射覆蓋的是一片，不是一條線。地上會出現三個圈 —— 往圈與圈的縫隙跑沒有用，要整個離開。',
  /* WING 翼 */
  falke: '俯衝時它會下降並翻滾。它撞的是你**現在**的位置，所以持續移動就永遠撞不到。',
  krahe: '它在你頭上，地面掩體對它無效。影子的位置就是它的正下方 —— 影子壓到你身上時抬頭。',
  schwarm: '雙掛彈脫離的那一刻落點就定了。它投的是範圍，往外跑，不要往它下面躲。',
  horcher: '水平雷達盤轉速加快五倍＝它正在加成友軍。它飛得最高、離你最遠，通常也最晚被處理。',
  /* RING 環 */
  reifen: '直立環滾過來，攻擊時暴轉。它跟 ROLLE 的差別是它會纏住你 —— 別讓它把你逼到牆邊。',
  kranz: '環上三管是**徑向朝外**的：它的死角是貼身與遠處，最危險的是中距離。選一邊，不要停在中間。',
  wirbel: '內環收縮＝它正在把你往中間吸。被吸到中心會吃一次範圍傷害，**先打斷它**比硬走出去快。',
  nest: '它自己不打你，但它會一直生。場面越來越擠的時候先找它 —— 不然你清的是它的產量不是它。',
  /* 深層 12 隻（規格 §2.2b 的第三軸） */
  spross: '突刺往右偏三十度，所以它的攻擊線不在牠面朝的方向上。照正規錐的方式閃會閃到刀口上。',
  faden: '前端那根細絲不是武器，是感測器 —— 它在替深層的其他東西標定。它的沉默才是威脅。',
  kriecher: '三條腳而且位置錯開，所以它的移動有一個永遠對不齊的節奏。用節奏預判它會失準。',
  laich: '三顆卵各自脈動、大小不一。脈動最快的那一顆是下一個會動作的 —— 那是唯一的預告。',
  pforte: '**你看得穿它** —— 那個洞就是門本身。門後看到的東西不是背景，是它的一部分。',
  zunge: '上翹的是舌不是砲，它會抽打而不是射擊。判定沿著舌甩出去的整條弧，不是一個點。',
  klumpen: '三顆球黏成一團，滾起來會晃 —— 它的行進線是歪的，直覺的攔截點會偏。',
  iris: '洞就是瞳孔。蓄力時瞳孔放大，所以**洞變大＝它要出手了** —— 那是全表唯一會動的洞。',
  rippe: '兩端一粗一細，像一根斷掉的骨。細的那一端才是它的正面，粗的那端不會攻擊。',
  motte: '一大一小的翼讓它的飛行軌跡永遠是歪的。它不是在閃你，它只是飛不直。',
  maul: '環是嘴，兩顆錯位的齒會咬合。咬合的瞬間判定在環心 —— 站在環的正下方最危險。',
  leier: '三根弦依序繃緊＝倒數，跟 SPULE 的線圈是同一種語法。第三根繃緊時就該不在那條線上了。'
};

/** 沒有手寫文案時的退路。**刻意寫得像「還沒寫」而不是硬掰一句** ——
    假裝有情報比承認沒有更糟。 */
export var CODEX_FALLBACK = '（尚未建立應對紀錄）';

export function codexTip(id) { return CODEX_TIP[id] || CODEX_FALLBACK; }

/** 數值條的正規化範圍（讓玩家比較，不是讀原始數字）。
    上限刻意訂在**全表最大值附近**而不是剛好等於它 ——
    等於的話那一隻會是滿格，讀起來像「這是上限」而不是「這是最大的一隻」。 */
export var CODEX_BARS = [
  { k: 'spd', n: '速度', max: 4.0 },
  { k: 'rng', n: '射程', max: 9.0 },
  { k: 'tTel', n: '預備', max: 1.3, note: '越短越難反應', invert: true },
  { k: 'h', n: '體型', max: 2.6 }
];

/** BOSS 的數值條**另外一組上限**。
    共用小怪那組的話 TURM（14 高）與 SCHLANGE（22 長）會直接滿格爆表，
    而且所有 Boss 的體型條都會長得一模一樣 —— 比較就失效了。
    Boss 只跟 Boss 比，這是「相對於這批資料的極值」那條老規矩。 */
export var CODEX_BARS_BOSS = [
  { k: 'spd', n: '速度', max: 2.0 },
  { k: 'rng', n: '射程', max: 15.0 },
  { k: 'tTel', n: '預備', max: 2.2, note: '越短越難反應', invert: true },
  /* 上限吃得下 WELTACHSE 的 48 高與 SCHLANGE 的 22 長。
     ⚠️ 原型再長出更大的 Boss 時這裡要跟著調 —— `codex.test.ts` 有一條
     「上限涵蓋得住每一隻」會先紅，那是提醒不是故障。 */
  { k: 'h', n: '高', max: 50.0 },
  { k: 'w', n: '寬', max: 23.0 }
];

/** BOSS 分組的說明（規格 §5.2「家族歸屬」）。 */
export var BOSS_INFO = {
  zh: 'BOSS',
  sil: '不屬於任何剪影家族',
  note: '家族是給「一次出現二十隻」的東西用的規則。**BOSS 一次只有一隻，'
    + '所以它可以也應該是全新的形狀** —— 你對「東西應該長成八種樣子之一」的預期會落空，'
    + '那正是設計的一部分。'
};

/** 原型的 `BOSSES` 表裡缺階段描述的那幾隻，從規格 §5.2 補回來。

    **只有 WALZE 缺。** 它是最早做的那一隻，二階段是後來用猴子補丁加上去的
    （原型 3072–3092），所以欄位表裡沒有 `p2`。ZENIT 同樣被補丁過，
    但它的 `p2` 有寫。這一份是文案層的補丁，不要回頭去改機械產生的
    `data/bosses.ts`（下一次移植就沒了）。 */
export var BOSS_PHASE_EXTRA = {
  walze: ['車體下沉、砲塔自轉連射 —— 撞擊判定變大，同時多一路直射（紅＋橙）']
};

/** 階段的通則（規格 §2.4 給 BOSS 的讀法）。 */
export var BOSS_RULE = {
  zh: '階段',
  rule: '**打亮的地方；牠變形了就是規則變了。** 二階段一律用造型講機制，不是把數字調大 ——'
    + '殼會開、刃會掉、塔會倒、蛇會散，你不用讀 UI 就知道該換打法了。'
};
